import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { useShallow } from 'zustand/shallow';
import useEditorStore from '../../../stores/useEditorStore';
import useAxesAlignmentStore from '../../../stores/useAxesAlignmentStore';
import { ThreeAxisMaterials as axisMaterials } from '../../../constants/axisConfig.js';
import configureGizmo, { scaleTranslateArrowheads } from '../../../utils/configureTransformControls.js';

const FineTuneControls = () => {
  // Extract scene, camera, renderer, mesh and controls from store
  const { sceneRefs, scene, camera, renderer, shadedMesh, controls } = useEditorStore(
    useShallow((s) => ({
      sceneRefs: s.sceneRefs,
      scene: s.sceneRefs.scene,
      camera: s.sceneRefs.camera,
      renderer: s.sceneRefs.renderer,
      shadedMesh: s.sceneRefs.shadedMesh,
      controls: s.sceneRefs.controls,
    }))
  );
  const setSceneRefs = useEditorStore((s) => s.setSceneRefs);
  const replaceCameraAndControls = useEditorStore((s) => s.replaceCameraAndControls);
  const isAligned = useAxesAlignmentStore((s) => s.isAligned);

  // Refs to hold transform controls and originals
  const translateRef = useRef(null);
  const rotateRef = useRef(null);
  const originalCameraRef = useRef(null);
  const originalControlsRef = useRef(null);

  // Capture original camera & controls once
  useEffect(() => {
    originalCameraRef.current = camera;
    originalControlsRef.current = controls;
  }, []);

  useEffect(() => 
  {
    console.log(`isAligned: ${isAligned}, renderer: ${renderer}, scene: ${scene}, camera: ${camera}, controls: ${controls}`)
    if (!isAligned || !renderer || !scene || !camera || !controls) return;
    console.log("Mounting new camera and controls...")

    // Disable the original orbit controls
    originalControlsRef.current.enabled = false;

    // Build an orthographic camera in world units
    const aspect = renderer.domElement.clientWidth / renderer.domElement.clientHeight;
    const frustumSize = 100;
    const orthoCam = new THREE.OrthographicCamera(
      -frustumSize * aspect / 2,
       frustumSize * aspect / 2,
       frustumSize / 2,
      -frustumSize / 2,
       1,
       1000
    );

    // Match position & orientation
    orthoCam.position.copy(camera.position);
    orthoCam.quaternion.copy(camera.quaternion);
    orthoCam.updateProjectionMatrix();

    // Dispose old OrbitControls and make a fresh one
    // controls.dispose();
    const orthoControls = new OrbitControls(orthoCam, renderer.domElement);
    orthoControls.enableDamping = true;
    orthoControls.screenSpacePanning = true;
    orthoControls.enableRotate = false;
    // orthoControls.enabled =false;

    // Swap into Zustand store
    replaceCameraAndControls(orthoCam, orthoControls);

    // Create and add TransformControls
    const translate = new TransformControls(orthoCam, renderer.domElement);
    translate.setMode('translate');
    translate.setSize(1.2);
    translate.attach(shadedMesh);
    configureGizmo(translate);
    scaleTranslateArrowheads(translate, { radius: 1.8, length: 1.4 });
    scene.add(translate.getHelper());
    translateRef.current = translate;
    
    const rotate = new TransformControls(orthoCam, renderer.domElement);
    rotate.setMode('rotate');
    rotate.attach(shadedMesh);
    rotate.setSize(1.2);
    configureGizmo(rotate);
    scene.add(rotate.getHelper());
    rotateRef.current = rotate;

    // When dragging, disable orbit
    [translate, rotate].forEach((tc) => {
      tc.addEventListener('dragging-changed', (e) => {
        orthoControls.enabled = !e.value;
      });
    });

    // Axis-visibility logic
    const updateVisibility = () => {
      const dir = new THREE.Vector3();
      orthoCam.getWorldDirection(dir);
      const dots = {
        x: Math.abs(dir.dot(new THREE.Vector3(1, 0, 0))),
        y: Math.abs(dir.dot(new THREE.Vector3(0, 1, 0))),
        z: Math.abs(dir.dot(new THREE.Vector3(0, 0, 1))),
      };

      const maxDot = Math.max(dots.x, dots.y, dots.z);
      translate.showX = translate.showY = translate.showZ = false;
      const eGizmo = rotate._gizmo.gizmo.rotate.getObjectByName('E');
      if (maxDot === dots.z) {
        translate.showX = translate.showY = true;
        eGizmo.material = axisMaterials.Z;
    } else if (maxDot === dots.x) {
        translate.showY = translate.showZ = true;
        eGizmo.material = axisMaterials.X;
    } else {
        translate.showX = translate.showZ = true;
        eGizmo.material = axisMaterials.Y;
      }
    };
    orthoControls.addEventListener('change', updateVisibility);
    updateVisibility();

    const handleResize = () => {
      if (renderer.domElement) {
        const newWidth = renderer.domElement.clientWidth;
        const newHeight = renderer.domElement.clientHeight;
        
        const newAspect = newWidth / newHeight;
        orthoCam.aspect = newAspect;

        renderer.setSize(newWidth, newHeight);

        orthoCam.left = -frustumSize * newAspect / 2;
        orthoCam.right = frustumSize * newAspect / 2;
        orthoCam.top = frustumSize / 2;
        orthoCam.bottom = -frustumSize / 2;
        orthoCam.updateProjectionMatrix();
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(renderer.domElement);

    // Cleanup on unmount or when alignment toggles off
    return () => {
      console.log('FineTuneControls unmounting');
      orthoControls.removeEventListener('change', updateVisibility);
      [translate, rotate].forEach((tc) => {
        tc.detach();
        scene.remove(tc.getHelper());
        tc.dispose();
      });
      orthoControls.dispose();

      // Restore original camera & controls
      originalControlsRef.current.enabled = true;
      replaceCameraAndControls(originalCameraRef.current, originalControlsRef.current);

      resizeObserver.unobserve(renderer.domElement);
      resizeObserver.disconnect();
    };
  }, [isAligned, shadedMesh, setSceneRefs]);

  return null;
};

export default FineTuneControls;
